import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PricingSection1 } from './pro-blocks/landing-page/pricing-sections/pricing-section-1';
import { CreditsExplanationModal } from './CreditsExplanationModal';
import { BuyCreditsSection } from './BuyCreditsSection';
import { InfoHint } from './InfoHint';
import { DataTablePagination } from './ui/data-table-pagination';
import { Button } from './ui/button';

interface CreditsActivityItem {
  id: string;
  balance: number;
  createdAt: string;
  usageType: string;
  notes: string | null;
  documentId: string | null;
  documentTitle: string | null;
  projectId: string | null;
  projectTitle: string | null;
}

interface PlanSelectionProps {
  currentTier?: 'free' | 'pro' | 'editorial';
  hideHeader?: boolean;
  cancelAtPeriodEnd?: boolean;
}

const PlanSelection: React.FC<PlanSelectionProps> = ({ currentTier, hideHeader, cancelAtPeriodEnd }) => {
  return <PricingSection1 currentTier={currentTier} defaultBillingPeriod="annually" hideHeader={hideHeader} cancelAtPeriodEnd={cancelAtPeriodEnd} />;
};

export const UsageAndBilling: React.FC = () => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activity, setActivity] = useState<CreditsActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [storageInfo, setStorageInfo] = useState<{ usedBytes: number; quotaBytes: number; usedMb: number; quotaMb: number } | null>(null);
  const [tierLimits, setTierLimits] = useState<{
    tier: string;
    maxProjects: number | null;
    maxDocumentsPerProject: number | null;
    maxResourcesPerProject: number | null;
    currentProjectCount: number;
    projects: Array<{
      id: string;
      title: string;
      documentCount: number;
      resourceCount: number;
    }>;
  } | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    hasSubscription: boolean;
    tier: string;
    status: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    billingCurrency: string | null;
    billingInterval: string | null;
  } | null>(null);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      } else {
        window.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const fetchActivity = useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/credits-activity`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActivity(data.activity || []);
      }
    } catch (error) {
      console.error('Error fetching credits activity:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const fetchStorageInfo = useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/settings/storage`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStorageInfo(data);
      }
    } catch (error) {
      console.error('Error fetching storage info:', error);
    }
  }, [token]);

  React.useEffect(() => {
    fetchStorageInfo();
  }, [fetchStorageInfo]);

  const fetchTierLimits = useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/settings/tier-limits`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTierLimits(data);
      }
    } catch (error) {
      console.error('Error fetching tier limits:', error);
    }
  }, [token]);

  React.useEffect(() => {
    fetchTierLimits();
  }, [fetchTierLimits]);

  const fetchSubscriptionInfo = useCallback(async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/stripe/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  }, [token]);

  React.useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  useEffect(() => {
    const upgrade = searchParams.get('upgrade');
    if (upgrade === 'success') {
      showToast(t('plan_updated_successfully'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast, t]);

  useEffect(() => {
    const creditPurchase = searchParams.get('creditPurchase');
    if (creditPurchase === 'success') {
      showToast(t('credit_purchase_success'));
      fetchActivity();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast, t, fetchActivity]);

  const scrollToBuyCredits = useCallback((): void => {
    document.getElementById('buy-credits')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', '#buy-credits');
  }, []);

  useEffect(() => {
    if (window.location.hash === '#buy-credits') {
      scrollToBuyCredits();
    }
  }, [scrollToBuyCredits]);

  const handleManageSubscription = async (): Promise<void> => {
    if (!token) {
      return;
    }

    setIsManagingSubscription(true);
    try {
      const returnUrl = window.location.href;
      const response = await fetch(`${API_BASE}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ returnUrl }),
      });

      if (response.ok) {
        const { portalUrl } = await response.json();
        window.location.href = portalUrl;
      } else {
        const error = await response.json();
        console.error('Error creating portal session:', error);
        alert(t('error_managing_subscription'));
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      alert(t('error_managing_subscription'));
    } finally {
      setIsManagingSubscription(false);
    }
  };

  React.useEffect(() => {
    const handleResourceUploaded = (): void => {
      fetchStorageInfo();
      fetchTierLimits();
    };

    window.addEventListener('resource-uploaded', handleResourceUploaded);

    return () => {
      window.removeEventListener('resource-uploaded', handleResourceUploaded);
    };
  }, [fetchStorageInfo, fetchTierLimits]);

  const getUsageTypeLabel = React.useCallback((usageType: string): string => {
    const normalizedUsageType = usageType.replace(/-/g, '_');
    const translationKey = `usage_type_${normalizedUsageType}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : usageType;
  }, [t]);

  const columns = useMemo<ColumnDef<CreditsActivityItem>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: ({ column }) => {
          return (
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded text-foreground"
            >
              {t('date')}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUpDown className="h-4 w-4" />
              )}
            </button>
          );
        },
        cell: ({ row }) => {
          const date = new Date(row.original.createdAt);
          return <div>{date.toLocaleString()}</div>;
        },
        sortingFn: (rowA, rowB) => {
          const dateA = new Date(rowA.original.createdAt).getTime();
          const dateB = new Date(rowB.original.createdAt).getTime();
          return dateA - dateB;
        },
      },
      {
        accessorKey: 'balance',
        header: ({ column }) => {
          return (
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded text-foreground"
            >
              {t('balance')}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUpDown className="h-4 w-4" />
              )}
            </button>
          );
        },
        cell: ({ row }) => {
          const balance = row.original.balance;
          return (
            <div style={{ fontWeight: '600' }}>
              {balance > 0 ? '+' : ''}{balance}
            </div>
          );
        },
        sortingFn: (rowA, rowB) => {
          return rowA.original.balance - rowB.original.balance;
        },
      },
      {
        accessorKey: 'usageType',
        header: ({ column }) => {
          return (
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded text-foreground"
            >
              {t('usage_type')}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUpDown className="h-4 w-4" />
              )}
            </button>
          );
        },
        cell: ({ row }) => {
          return <div>{getUsageTypeLabel(row.original.usageType)}</div>;
        },
      },
      {
        accessorKey: 'notes',
        header: () => {
          return <div>{t('notes')}</div>;
        },
        cell: ({ row }) => {
          return <div>{row.original.notes || '-'}</div>;
        },
      },
      {
        accessorKey: 'documentTitle',
        header: () => {
          return <div>{t('document_title')}</div>;
        },
        cell: ({ row }) => {
          return <div>{row.original.documentTitle || '-'}</div>;
        },
      },
      {
        accessorKey: 'projectTitle',
        header: () => {
          return <div>{t('project_title')}</div>;
        },
        cell: ({ row }) => {
          return <div>{row.original.projectTitle || '-'}</div>;
        },
      },
    ],
    [t, getUsageTypeLabel]
  );

  const table = useReactTable({
    data: activity,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
    },
  });

  const currentBalance = useMemo(() => {
    return activity.reduce((sum, item) => sum + item.balance, 0);
  }, [activity]);

  const projectsExceedingLimits = useMemo(() => {
    if (!tierLimits || (!tierLimits.maxDocumentsPerProject && !tierLimits.maxResourcesPerProject)) {
      return [];
    }
    return tierLimits.projects.filter((project) => {
      const exceedsDocuments = tierLimits.maxDocumentsPerProject !== null && project.documentCount > tierLimits.maxDocumentsPerProject;
      const exceedsResources = tierLimits.maxResourcesPerProject !== null && project.resourceCount > tierLimits.maxResourcesPerProject;
      return exceedsDocuments || exceedsResources;
    });
  }, [tierLimits]);

  const hasExceededLimits = useMemo(() => {
    const projectsLimitExceeded = tierLimits && tierLimits.maxProjects !== null && tierLimits.currentProjectCount >= tierLimits.maxProjects;
    const projectLimitsExceeded = projectsExceedingLimits.length > 0;
    const storageLimitExceeded = storageInfo && storageInfo.usedBytes > storageInfo.quotaBytes;
    return projectsLimitExceeded || projectLimitsExceeded || storageLimitExceeded;
  }, [tierLimits, projectsExceedingLimits, storageInfo]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        {t('loading')}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      style={{ padding: '0.25rem', maxWidth: '100%', overflowX: 'hidden', outline: 'none' }}
      className="sm:p-2"
    >
      <div style={{ marginBottom: '1.5rem', textAlign: 'center', maxWidth: '42rem', marginLeft: 'auto', marginRight: 'auto', padding: '0 0.25rem' }} className="sm:px-2">
        <h2 style={{ fontSize: '1.875rem', fontWeight: '700', marginBottom: '1.5rem', color: 'var(--foreground)' }}>
          {t('pricing_title')}
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>
          {t('pricing_subtitle')}
        </p>
        <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
          {t('pricing_description')}
        </p>
      </div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', padding: '0 0.25rem' }} className="sm:px-2">
        {t('usage_summary')}
      </h2>
      <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '0.75rem', padding: '0 0.25rem' }} className="sm:px-2 sm:gap-4">
        {tierLimits && (
          <div style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '0.5rem' }} className="p-6">
            <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>
              {t('projects')}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--foreground)' }}>
              {tierLimits.currentProjectCount}
              {tierLimits.maxProjects !== null && (
                <span style={{ fontSize: '1rem', fontWeight: '400', color: 'var(--muted-foreground)', marginLeft: '0.5rem' }}>
                  / {tierLimits.maxProjects}
                </span>
              )}
            </div>
            {tierLimits.maxProjects !== null && (
              <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginTop: '0.25rem' }}>
                {tierLimits.currentProjectCount >= tierLimits.maxProjects ? (
                  <span style={{ color: 'var(--destructive)' }}>{t('limit_reached')}</span>
                ) : (
                  <span>{tierLimits.maxProjects - tierLimits.currentProjectCount} {t('remaining')}</span>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '0.5rem' }} className="p-6">
          <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {t('current_balance')}
            <InfoHint
              label={t('whats_a_credit')}
              footerAction={{
                label: t('whats_a_credit'),
                onClick: () => setCreditsModalOpen(true),
              }}
            >
              <p>{t('credit_balance_tooltip')}</p>
            </InfoHint>
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: currentBalance < 0 ? 'var(--destructive)' : 'var(--foreground)' }}>
            {currentBalance}
          </div>
          <Button
            variant="link"
            onClick={scrollToBuyCredits}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '0.875rem',
              marginTop: '0.25rem',
              textDecoration: 'underline',
              textUnderlineOffset: '0.25rem',
            }}
            className="text-muted-foreground"
          >
            {t('buy_extra_credits')}
          </Button>
        </div>
        {storageInfo && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '0.5rem',
            }}
            className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:p-4 sm:gap-4"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'used', value: storageInfo.usedBytes },
                      { name: 'available', value: Math.max(0, storageInfo.quotaBytes - storageInfo.usedBytes) },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="80%"
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                  >
                    <Cell key="used" fill={storageInfo.usedBytes > storageInfo.quotaBytes ? 'var(--destructive)' : 'var(--foreground)'} />
                    <Cell key="available" fill="var(--muted)" />
                  </Pie>
                  <Tooltip
                    formatter={(value) => {
                      if (typeof value !== 'number' || value === undefined) return '0 MB';
                      const mb = Math.round((value / (1024 * 1024)) * 100) / 100;
                      return `${mb} MB`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: 1, minWidth: 0 }} className="text-center sm:text-left">
              <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginBottom: '0.5rem' }}>
                {t('Storage') || 'Storage'}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--foreground)' }} className="sm:text-xl">
                {storageInfo.usedMb.toFixed(2)} MB / {storageInfo.quotaMb} MB
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', marginTop: '0.25rem' }}>
                {((storageInfo.usedBytes / storageInfo.quotaBytes) * 100).toFixed(1)}% {t('used') || 'used'}
              </div>
            </div>
          </div>
        )}
      </div>
      {projectsExceedingLimits.length > 0 && tierLimits && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--color-status-error-bg)',
            border: '1px solid var(--color-status-error-bg)',
            borderRadius: '0.5rem',
            marginLeft: '0.25rem',
            marginRight: '0.25rem',
          }}
          className="sm:mx-2"
        >
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--color-status-error-text)', marginBottom: '0.75rem' }}>
            {t('projects_exceeding_limits')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {projectsExceedingLimits.map((project) => {
              const exceedsDocuments = tierLimits.maxDocumentsPerProject !== null && project.documentCount > tierLimits.maxDocumentsPerProject;
              const exceedsResources = tierLimits.maxResourcesPerProject !== null && project.resourceCount > tierLimits.maxResourcesPerProject;
              return (
                <div
                  key={project.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--color-status-error-bg)',
                    borderRadius: '0.375rem',
                  }}
                >
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                    {project.title}
                  </div>
                  {exceedsDocuments && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-status-error-text)', marginBottom: '0.25rem' }}>
                      • {t('documents_label')} {project.documentCount} / {tierLimits.maxDocumentsPerProject} ({t('limit_label')} {tierLimits.maxDocumentsPerProject} {t('documents_per_project')})
                    </div>
                  )}
                  {exceedsResources && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-status-error-text)' }}>
                      • {t('resources_label')} {project.resourceCount} / {tierLimits.maxResourcesPerProject} ({t('limit_label')} {tierLimits.maxResourcesPerProject} {t('resources_per_project')})
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {hasExceededLimits && (
        <div
          style={{
            marginBottom: '1.5rem',
            marginTop: '1.5rem',
            padding: '0.75rem',
            backgroundColor: 'var(--color-status-error-bg)',
            border: '1px solid var(--color-status-error-bg)',
            borderRadius: '0.5rem',
            marginLeft: '0.25rem',
            marginRight: '0.25rem',
          }}
          className="sm:mx-2 sm:my-4"
        >
          <p style={{ fontSize: '0.875rem', color: 'var(--color-status-error-text)', margin: 0, wordWrap: 'break-word' }}>
            {t('exceeded_limits_message')}
          </p>
        </div>
      )}
      <div style={{ marginTop: '4rem' }}>
        <PlanSelection currentTier={tierLimits?.tier as 'free' | 'pro' | 'editorial' | undefined} hideHeader={true} cancelAtPeriodEnd={subscriptionInfo?.cancelAtPeriodEnd} />
      </div>
      <CreditsExplanationModal
        open={creditsModalOpen}
        onOpenChange={setCreditsModalOpen}
      />
      {subscriptionInfo?.hasSubscription && (
        <div
          style={{
            marginTop: '2rem',
            marginLeft: '0.25rem',
            marginRight: '0.25rem',
            padding: '1rem',
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
          }}
          className="sm:mx-2"
        >
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--foreground)' }}>
            {t('subscription_info')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--muted-foreground)' }}>{t('subscription_status')}</span>
              <span style={{
                fontWeight: '500',
                color: subscriptionInfo.status === 'active' ? 'var(--color-status-applied-text)' :
                       subscriptionInfo.status === 'past_due' ? 'var(--color-status-error-text)' : 'var(--muted-foreground)'
              }}>
                {subscriptionInfo.status === 'active' ? t('status_active') :
                 subscriptionInfo.status === 'past_due' ? t('status_past_due') :
                 subscriptionInfo.status === 'canceled' ? t('status_canceled') :
                 subscriptionInfo.status || '-'}
              </span>
            </div>
            {subscriptionInfo.currentPeriodEnd && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted-foreground)' }}>
                  {subscriptionInfo.cancelAtPeriodEnd ? t('subscription_ends') : t('next_billing_date')}
                </span>
                <span style={{ fontWeight: '500' }}>
                  {new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString()}
                </span>
              </div>
            )}
            {subscriptionInfo.cancelAtPeriodEnd && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'var(--color-status-rejected-bg)',
                border: '1px solid var(--color-status-rejected-bg)',
                borderRadius: '0.25rem',
                fontSize: '0.875rem',
                color: 'var(--color-status-rejected-text)',
              }}>
                {t('subscription_will_be_canceled')}
              </div>
            )}
            {subscriptionInfo.status === 'past_due' && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'var(--color-status-error-bg)',
                border: '1px solid var(--color-status-error-bg)',
                borderRadius: '0.25rem',
                fontSize: '0.875rem',
                color: 'var(--color-status-error-text)',
              }}>
                {t('payment_failed_update_method')}
              </div>
            )}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Button
              variant="outline"
              onClick={handleManageSubscription}
              disabled={isManagingSubscription}
              style={{ width: '100%' }}
            >
              {isManagingSubscription ? t('loading') : t('manage_subscription')}
            </Button>
          </div>
        </div>
      )}
      <BuyCreditsSection />
      <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', marginTop: '3rem', padding: '0 0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} className="sm:px-2 sm:mt-8">
        {t('credits_usage')}{' '}
        <Button
          variant="link"
          onClick={() => setCreditsModalOpen(true)}
          style={{ padding: 0, height: 'auto', fontSize: '1.25rem', fontWeight: '600', color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '0.25rem' }}
          className="text-muted-foreground"
        >
          ({t('whats_a_credit')})
        </Button>
      </h2>
      <div
        style={{
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          marginLeft: '0.25rem',
          marginRight: '0.25rem',
        }}
        className="overflow-x-auto sm:mx-2"
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                  {t('no_credits_activity')}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '1rem' }}>
          <DataTablePagination table={table} />
        </div>
      </div>
    </div>
  );
};

